import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { TransactionDetail } from '../interfaces/transaction-details';
import { TotalFeesCollected } from '../interfaces/total-fees-collected';
import { map, PaymentInvoiceData, ClubUserRefund, ClubProgramIncomeCounter } from '../models';
import { RefundPayment } from '../interfaces';
import { HttpService } from './http.service';
import * as _ from 'lodash';

const FeeColors = ['#8bddd6', '#61acd5', '#9f9fa0', '#668e8c', '#4e85b0', '#58595b', '#355954', '#225b82'];

@Injectable()
export class FinancialsService {
  private _url = '/api/Financials';

  constructor(private http: HttpClient, private httpService: HttpService) { }

  getTransactionDetails(clubProgramID: string | number): Observable<TransactionDetail[]> {
    return this.http.get<TransactionDetail[]>(this._url + '/get-transactionDetails/' + clubProgramID).pipe(
      map(
        res => _.sortBy(
          res.map((fee, index) => Object.assign(fee, {
            color: FeeColors[index % FeeColors.length],
            paymentdate: fee.paymentdate.slice(0, 10),
          })),
          ['paymentdate', 'transactionID']
        ).reverse()
      )
    );
  }

  getTotalCollectedFees(clubProgramID: string | number): Observable<TotalFeesCollected[]> {
    return this.http.get<TotalFeesCollected[]>(this._url + '/get-totalCollectedFees/' + clubProgramID).pipe(
      map(res => res.map((fee, index) => Object.assign(fee, { color: FeeColors[index % FeeColors.length] })))
    );
  }

  getFinancialsTopFourCounts(clubProgramID: number, feeID: number = 0): Observable<any> {
    return this.http.get<ClubProgramIncomeCounter>(this._url + '/get-financialsTopFourCounts/' + clubProgramID, { params: { feeID } } as any);
  }

  refund(refundPayment: RefundPayment, userId: number) {
    return this.httpService.post(`${this._url}/refundPayment/${refundPayment.ClubprogramfeeID}/${userId}`, refundPayment);
  }

  getRefundsList(clubProgramID: number): Observable<ClubUserRefund[]> {
    return this.httpService.get(`${this._url}/getRefundsList/${clubProgramID}`);
  }

  getInvoiceData(paymentId: number) {
    return this.httpService.get(`${this._url}/getPaymentInvoiceData/${paymentId}`).pipe(map(data => new PaymentInvoiceData(data)));
  }

  getReport(clubId: number, clubProgramID = 0) {
    return this.httpService.get(`${this._url}/financials-report/${clubId}/${clubProgramID}`);
  }

  getMonthlyReport(clubId: number, clubProgramID = 0) {
    return this.httpService.get(`${this._url}/financials-monthly-report/${clubId}/${clubProgramID}`);
  }

  getFinancialReportMonthlyChartData(monthlyReport) {
    const projectedIncomeData = {};
    const grossVolumeData = {};
    const pastDueData = {};
    const outstandingData = {};

    const labels = [];
    if (monthlyReport.length) {
      for (let year = monthlyReport[0].year; year <= monthlyReport[monthlyReport.length - 1].year; year++) {
        for (let month = 1; month < 13; month++) {
          if (year === monthlyReport[0].year && month >= monthlyReport[0].month) {
            labels.push(`${year}-${month}`);
          } else if (year > monthlyReport[0].year && month <= monthlyReport[monthlyReport.length - 1].month) {
            labels.push(`${year}-${month}`);
          } else if (year > monthlyReport[0].year && year < monthlyReport[monthlyReport.length - 1].year) {
            labels.push(`${year}-${month}`);
          } else {
            continue;
          }

          const data = monthlyReport.find(r => r.year === year && r.month === month);
          projectedIncomeData[`${year}-${month}`] = data
            ? data.budget - data.discountAmount - data.scholarshipAmount - data.creditAmount
            : 0;
          grossVolumeData[`${year}-${month}`] = data ? data.paidAmount : 0;
          pastDueData[`${year}-${month}`] = data ? data.pastDueAmount : 0;
          outstandingData[`${year}-${month}`] = data ? data.remainingAmount : 0;
        }
      }
    }

    return {
      projectedIncomeData,
      grossVolumeData,
      pastDueData,
      outstandingData,
    };
  }

  getEventFinancialReportData(clubID: number, year = 0) {
    return this.httpService.get(`${this._url}/getEventFinancialReportData/${clubID}/${year}`);
  }

  getEventFinancialReportChartData(clubID: number, year = 0) {
    return this.httpService.get(`${this._url}/getEventFinancialReportChartData/${clubID}/${year}`);
  }

  getFacilityFinancialReportData(clubID: number, year = 0) {
    return this.httpService.get(`${this._url}/getFacilityFinancialReportData/${clubID}/${year}`);
  }

  getFacilityFinancialReportChartData(clubID: number, year = 0) {
    return this.httpService.get(`${this._url}/getFacilityFinancialReportChartData/${clubID}/${year}`);
  }

  getClubFinancialFacilityPaymentHistoryList(clubID: number, facilityInvoiceID: number) {
    return this.httpService.get(`${this._url}/get-club-financial-facility-payment-history-list/${clubID}/${facilityInvoiceID}`);
  }
  addUpdateFacilityFinancialReportData(obj: any) {
    return this.httpService.post(this._url + '/add-update-facility-financial-report-data/', obj);
  }

  deleteFacilityFinancialReportData(obj: any) {
    const httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'responseType': 'text'
    }),
      body: {
        Deletedby: obj.Deletedby,
        FacilityInvoiceIDList: obj.FacilityInvoiceIDList,
      }
    };

    return this.http.delete(this._url + '/delete-facility-financial-report-data', httpOptions);
  }

  updateFacilityFinancialPaymentHistory(obj: any) {
    return this.httpService.post(this._url + '/update-facility-financial-payment-history/', obj);
  }

  deleteFinancialFacilityLogPayment(obj: any) {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'responseType': 'text'
      }),
      body: {
        FacilityPaymentID: obj.FacilityPaymentID,
        FacilityInvoiceID: obj.FacilityInvoiceID,
      }
    };

    return this.http.delete(this._url + '/delete-financial-facility-log-payment', httpOptions);
  }

  getFinancialReports(userID: number, clubID: number) {
    return this.httpService.get(`/api/Mobile/financial-reports-current-by-club-id-mobile/${userID}/${clubID}`);
  }
}



// WEBPACK FOOTER //
// ./src/app/services/financials.service.ts